/**
 * Routed Dispute Items Panel Component
 * 
 * Shows items routed outside Metro 2:
 * - Inquiry Flow Required
 * - Personal Info Flow Required
 * - Data Breach Flow Required
 * - Factual Dispute Recommended
 * - Evidence Needed Before Metro 2
 */

import React from 'react';
import {
  ExternalLink,
  Eye,
  Shield,
  AlertCircle,
  FileText,
  Upload,
  ArrowRight,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';

// Route configurations
const ROUTE_CONFIGS = {
  inquiry_flow_required: {
    label: 'Inquiry Flow Required',
    icon: Eye,
    color: 'cyan',
    bgClass: 'bg-cyan-50 dark:bg-cyan-900/20',
    borderClass: 'border-cyan-200 dark:border-cyan-700',
    textClass: 'text-cyan-700 dark:text-cyan-300',
    description: 'Inquiries must be disputed through the dedicated Inquiry dispute flow.',
    action: 'Start Inquiry Dispute',
    actionPath: '/self-repair/disputes/factual',
  },
  personal_info_flow_required: {
    label: 'Personal Info Flow Required',
    icon: Shield,
    color: 'indigo',
    bgClass: 'bg-indigo-50 dark:bg-indigo-900/20',
    borderClass: 'border-indigo-200 dark:border-indigo-700',
    textClass: 'text-indigo-700 dark:text-indigo-300',
    description: 'Personal information items must be disputed through the Personal Info flow.',
    action: 'Start Personal Info Dispute',
    actionPath: '/self-repair/disputes/factual',
  },
  data_breach_flow_required: {
    label: 'Data Breach Flow Required',
    icon: AlertCircle,
    color: 'red',
    bgClass: 'bg-red-50 dark:bg-red-900/20',
    borderClass: 'border-red-200 dark:border-red-700',
    textClass: 'text-red-700 dark:text-red-300',
    description: 'These items may be related to data breach and require the Data Breach dispute flow.',
    action: 'Start Data Breach Dispute',
    actionPath: '/self-repair/disputes/factual',
  },
  factual_dispute_recommended: {
    label: 'Factual Dispute Recommended',
    icon: FileText,
    color: 'blue',
    bgClass: 'bg-blue-50 dark:bg-blue-900/20',
    borderClass: 'border-blue-200 dark:border-blue-700',
    textClass: 'text-blue-700 dark:text-blue-300',
    description: 'These items are disputable but lack Metro 2 technical conflicts. Factual dispute is recommended.',
    action: 'Start Factual Dispute',
    actionPath: '/self-repair/disputes/factual',
  },
  evidence_needed: {
    label: 'Evidence Needed Before Metro 2',
    icon: Upload,
    color: 'purple',
    bgClass: 'bg-purple-50 dark:bg-purple-900/20',
    borderClass: 'border-purple-200 dark:border-purple-700',
    textClass: 'text-purple-700 dark:text-purple-300',
    description: 'These items need supporting documentation before Metro 2 dispute can proceed.',
    action: 'Upload Evidence',
    actionPath: null,
  },
};

// Routed Item Card
const RoutedItemCard = ({ item, route }) => {
  const config = ROUTE_CONFIGS[route] || ROUTE_CONFIGS.factual_dispute_recommended;
  const IconComponent = config.icon;

  return (
    <div className={`p-3 rounded-lg border ${config.borderClass} ${config.bgClass}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <IconComponent className={`w-5 h-5 mt-0.5 ${config.textClass}`} />
          <div>
            <h4 className="font-medium text-stone-800 dark:text-stone-200">
              {item.creditor_name || item.creditorName || 'Unknown'}
            </h4>
            <p className="text-sm text-stone-500 dark:text-stone-400">
              {item.account_number_masked || item.accountNumberMasked || '****'} • {item.item_type || 'Account'}
            </p>
            {item.reason && (
              <p className={`text-sm mt-1 ${config.textClass}`}>
                {item.reason}
              </p>
            )}
          </div>
        </div>
        <Badge variant="outline" className={config.textClass}>
          {config.label.split(' ')[0]}
        </Badge>
      </div>
    </div>
  );
};

// Route Section
const RouteSection = ({ route, items, onAction }) => {
  const config = ROUTE_CONFIGS[route];
  if (!config || !items || items.length === 0) return null;

  const IconComponent = config.icon;

  return (
    <div className={`rounded-xl border ${config.borderClass} overflow-hidden`}>
      {/* Section Header */}
      <div className={`p-4 ${config.bgClass}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-white/80 dark:bg-stone-900/50">
              <IconComponent className={`w-5 h-5 ${config.textClass}`} />
            </div>
            <div>
              <h3 className={`font-semibold ${config.textClass}`}>
                {config.label} ({items.length})
              </h3>
              <p className="text-sm text-stone-500 dark:text-stone-400">
                {config.description}
              </p>
            </div>
          </div>
          {config.actionPath && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onAction?.(route, items)}
              className={`${config.textClass} border-current`}
            >
              {config.action}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>

      {/* Items */}
      <div className="p-4 space-y-2">
        {items.map((item, idx) => (
          <RoutedItemCard key={item.item_id || idx} item={item} route={route} />
        ))}
      </div>
    </div>
  );
};

// Main Routed Dispute Items Panel
const RoutedDisputeItemsPanel = ({
  inquiryItems = [],
  personalInfoItems = [],
  dataBreachItems = [],
  factualItems = [],
  evidenceNeededItems = [],
  onNavigate,
  onUploadEvidence,
  compact = false,
}) => {
  // Group all routed items
  const routedGroups = {
    inquiry_flow_required: inquiryItems,
    personal_info_flow_required: personalInfoItems,
    data_breach_flow_required: dataBreachItems,
    factual_dispute_recommended: factualItems,
    evidence_needed: evidenceNeededItems,
  };

  // Count total routed items
  const totalRouted = Object.values(routedGroups).reduce((sum, items) => sum + items.length, 0);

  // Handle action
  const handleAction = (route, items) => {
    const config = ROUTE_CONFIGS[route];
    if (route === 'evidence_needed') {
      onUploadEvidence?.(items);
    } else if (config?.actionPath) {
      onNavigate?.(config.actionPath, items);
    }
  };

  if (totalRouted === 0) {
    return null;
  }

  return (
    <Card className="border-stone-200 dark:border-stone-700" data-testid="routed-dispute-items-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2 text-stone-800 dark:text-stone-200">
              <ExternalLink className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              Routed Outside Metro 2
            </CardTitle>
            <CardDescription className="text-stone-500 dark:text-stone-400">
              These items cannot be disputed through Metro 2 and have been routed to appropriate flows.
            </CardDescription>
          </div>
          <Badge variant="outline" className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
            {totalRouted} item{totalRouted !== 1 ? 's' : ''}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Important Notice */}
        <Alert className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700">
          <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <AlertDescription className="text-amber-700 dark:text-amber-300">
            These items were routed outside Metro 2 because they do not contain Metro 2 technical conflicts. 
            They may still be disputable through their dedicated flows.
          </AlertDescription>
        </Alert>

        {/* Route Sections */}
        {Object.entries(routedGroups).map(([route, items]) => (
          <RouteSection 
            key={route}
            route={route}
            items={items}
            onAction={handleAction}
          />
        ))}

        {/* Summary Actions */}
        {!compact && (
          <div className="flex flex-wrap gap-2 pt-4 border-t border-stone-200 dark:border-stone-700">
            {factualItems.length > 0 && (
              <Button 
                variant="outline" 
                onClick={() => handleAction('factual_dispute_recommended', factualItems)}
              >
                <FileText className="w-4 h-4 mr-2" />
                Send All to Factual Queue ({factualItems.length})
              </Button>
            )}
            {evidenceNeededItems.length > 0 && (
              <Button 
                variant="outline"
                onClick={() => handleAction('evidence_needed', evidenceNeededItems)}
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Evidence ({evidenceNeededItems.length})
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RoutedDisputeItemsPanel;
export { RoutedItemCard, RouteSection, ROUTE_CONFIGS };
